import Head from 'next/head';
import type { NextPage } from 'next';
import type { AppProps } from 'next/app';
import type { ReactElement, ReactNode } from 'react';
import { useRouter } from 'next/router';

import { HostConsentBridge } from '../shared/observability/HostConsentBridge';
import { RumInitializer } from '../shared/observability/RumInitializer';
import { useListingsPageImplementation } from '../lib/document/useListingsPageImplementation';

// The single place global CSS may be imported from under the Pages Router. A
// folded surface adds its global stylesheet here; anything scoped stays a CSS
// Module colocated with its component.
//
// The folded conversions-manager surface ships element-selector-only globals
// (a normalize.css derivative + base typography). Turbopack rejects global CSS
// from any file except _app, and also rejects element-only selectors inside a
// *.module.css, so a getLayout-side import is impossible — it lands here, and
// the scoping compromise is documented in
// src/conversions-manager/styles/globals.css's header.
//
// The homepage surface is NOT imported here: its legacy-EDS base is scoped into
// src/homepage/styles/homepageGlobal.module.scss and imported from the
// homepage route graph (its getLayout), so its CSS chunk loads only on `/home`
// documents. See homepage-fold-decisions.md D1.
//
// The event-dashboard ad-preview stylesheet is imported here rather than from
// the surface's CSS Module: its components pass literal `fb-*`/`eb-ads-*` class
// names, and Turbopack rejects the `:global {}` block that used to keep those
// names unhashed inside a CSS Module.
import '../src/conversions-manager/styles/globals.css';
import '../src/event-dashboard/styles/eventDashboardGlobal.module.scss';
import '../src/event-dashboard/styles/fbStyles.global.scss';
import '@eventbrite/webapp-fonts/fonts.css';

/** Request-independent context a page's `getLayout` may need to build chrome. */
export type GetLayoutContext = {
  /** The current URL as the router sees it, used to reset error boundaries. */
  route: string;
};

/**
 * A page that brings its own shell. A folded surface exports `getLayout` from
 * each of its pages instead of relying on a nested layout, so surfaces can keep
 * different chrome (event nav vs organizer nav vs none) without this file
 * growing a global if/else per surface.
 */
export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement, ctx: GetLayoutContext) => ReactNode;
  suppressAnalytics?: boolean;
};

type AppPropsWithLayout = AppProps & { Component: NextPageWithLayout };

export default function App({ Component, pageProps }: AppPropsWithLayout) {
  const router = useRouter();
  useListingsPageImplementation(router);
  const getLayout = Component.getLayout ?? ((page: ReactElement) => page);

  return (
    <>
      {/* App-wide defaults. A page overrides any of these by rendering its own
          next/head with the same tag; the page's value wins. Viewport lives
          here rather than in _document, which Next disallows. */}
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>eb-web</title>
        <meta name="description" content="The unified Eventbrite web app." />
      </Head>
      {!Component.suppressAnalytics && (
        <>
          {/* Publishes Transcend consent as window.__EB_WEB_CONSENT__ before
              RumInitializer's effect reads it; sibling effects run in render
              order, so the bridge must stay first. */}
          <HostConsentBridge />
          <RumInitializer pathname={router.pathname} getPathname={() => router.pathname} />
        </>
      )}
      {getLayout(<Component {...pageProps} />, { route: router.asPath })}
    </>
  );
}
